import * as React from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, Loader2 } from "lucide-react";

const SUBJECT_OPTIONS = [
  "General Question",
  "Bug Report",
  "Feature Request",
  "Account Issue",
  // "Billing",
  "Other",
] as const;

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export function SupportForm() {
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [subject, setSubject] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot
  const [errors, setErrors] = React.useState<FormErrors>({});
  const [loading, setLoading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);

  const formLoadedAt = React.useRef(Date.now());

  function validateForm(): boolean {
    const newErrors: FormErrors = {};

    if (!name.trim()) newErrors.name = "Name is required";
    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Please enter a valid email";
    }
    if (!subject) newErrors.subject = "Please select a subject";
    if (!message.trim()) {
      newErrors.message = "Message is required";
    } else if (message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const res = await fetch("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject,
          message: message.trim(),
          website, // honeypot
          _formLoadedAt: formLoadedAt.current,
        }),
      });

      if (res.status === 429) {
        toast.error("Too many requests. Please try again later.");
        return;
      }

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error || "Something went wrong. Please try again.");
        return;
      }

      setSuccess(true);
      toast.success("Your message has been sent!");
    } catch {
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setName("");
    setEmail("");
    setSubject("");
    setMessage("");
    setErrors({});
    setSuccess(false);
    formLoadedAt.current = Date.now();
  }

  if (success) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-[#00EB90]/20 border-3 border-black">
          <CheckCircle2 className="w-8 h-8 text-[#00EB90]" />
        </div>
        <h3 className="text-xl font-bold font-['Sora']">Message Sent!</h3>
        <p className="text-sm text-muted-foreground text-center max-w-xs">
          We've received your message and will get back to you as soon as
          possible.
        </p>
        <Button variant="outline" onClick={handleReset} className="mt-2">
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Honeypot — hidden from real users */}
      <div
        className="absolute opacity-0 -z-10 h-0 overflow-hidden"
        aria-hidden="true"
      >
        <label htmlFor="website">Website</label>
        <input
          type="text"
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          placeholder="Your name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name)
              setErrors((prev) => ({ ...prev, name: undefined }));
          }}
          className="border-2 border-black rounded-xl focus-visible:ring-[#3F5EF4]"
          aria-invalid={!!errors.name}
        />
        {errors.name && (
          <p className="text-sm text-[#FF4444] font-medium">{errors.name}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="email">Email</Label>
        <Input
          id="email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email)
              setErrors((prev) => ({ ...prev, email: undefined }));
          }}
          className="border-2 border-black rounded-xl focus-visible:ring-[#3F5EF4]"
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p className="text-sm text-[#FF4444] font-medium">{errors.email}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Subject</Label>
        <Select
          value={subject}
          onValueChange={(val) => {
            setSubject(val);
            if (errors.subject)
              setErrors((prev) => ({ ...prev, subject: undefined }));
          }}
        >
          <SelectTrigger
            className="w-full border-2 border-black rounded-xl focus-visible:ring-[#3F5EF4]"
            aria-invalid={!!errors.subject}
          >
            <SelectValue placeholder="Select a topic" />
          </SelectTrigger>
          <SelectContent>
            {SUBJECT_OPTIONS.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.subject && (
          <p className="text-sm text-[#FF4444] font-medium">{errors.subject}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message</Label>
        <Textarea
          id="message"
          placeholder="Tell us how we can help..."
          rows={5}
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
            if (errors.message)
              setErrors((prev) => ({ ...prev, message: undefined }));
          }}
          className="border-2 border-black rounded-xl focus-visible:ring-[#3F5EF4] resize-none"
          aria-invalid={!!errors.message}
        />
        {errors.message && (
          <p className="text-sm text-[#FF4444] font-medium">{errors.message}</p>
        )}
      </div>

      <Button type="submit" disabled={loading} className="w-full" size="lg">
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sending...
          </>
        ) : (
          "Send Message"
        )}
      </Button>
    </form>
  );
}

export default SupportForm;
